import React from "react";
import { Col, Divider, Typography } from "antd";
import moment from "moment";

import {
  EuerDeclarationOfficeUsageSubformCalculationResults,
  EuerDeclarationOfficeUsageSubformFlatRate5EurInputs,
  EuerDeclarationOfficeUsageSubformInputs,
  EuerDeclarationSubformCalculationMethod,
} from "../../../../../../../utils/euer-declaration";
import { EuerDeclarationSubformStatus } from "../../../../types";
import DeclarationStatusChangeModal from "../../../../components/DeclarationStatusChangeModal";
import useTaxYearParam from "../../../../hooks/useTaxYearParam";
import { formatAmountInCents } from "../../../../../../../utils";
import colors from "../../../../../../../themes/colors";
import {
  HOME_OWNERSHIP_TYPE_LABELS,
  getOfficeUsageCalculationMethodLabels,
} from "./helpers";
import { SUBFORM_STATUSES, SUBFORM_STATUS_MAPPINGS } from "../../constants";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

const { Text } = Typography;
const { ListRow, Total } = DeclarationStatusChangeModal;

export interface OfficeUsageStatusChangeModalProps {
  calculationMethod: EuerDeclarationSubformCalculationMethod;
  inputs: EuerDeclarationOfficeUsageSubformInputs;
  calculationResults: EuerDeclarationOfficeUsageSubformCalculationResults;
  status: EuerDeclarationSubformStatus;
  isSmallBusiness: boolean;
  onSave: (status: EuerDeclarationSubformStatus) => Promise<void>;
  onClose: () => void;
  officeAreaShare?: number;
  totalNetAmount: number;
  totalVatAmount: number;
}

const isFlatRateMethod = (
  calculationMethod: EuerDeclarationSubformCalculationMethod,
  inputs: EuerDeclarationOfficeUsageSubformInputs
): inputs is EuerDeclarationOfficeUsageSubformFlatRate5EurInputs =>
  calculationMethod ===
  EuerDeclarationSubformCalculationMethod.OFFICE_USAGE_FLAT_RATE_5_EUR;

const OfficeUsageStatusChangeModal = ({
  calculationMethod,
  inputs,
  calculationResults,
  status,
  isSmallBusiness,
  onSave,
  onClose,
  officeAreaShare,
  totalNetAmount,
  totalVatAmount,
}: OfficeUsageStatusChangeModalProps) => {
  const [taxYear] = useTaxYearParam();

  // Get calculation method labels based on tax year
  const calculationMethodLabels =
    getOfficeUsageCalculationMethodLabels(taxYear);

  return (
    <DeclarationStatusChangeModal
      title="Häusliches Arbeitszimmer"
      year={taxYear}
      subtitle={calculationMethodLabels[calculationMethod]}
      status={status}
      statusOptions={SUBFORM_STATUSES}
      statusMapping={SUBFORM_STATUS_MAPPINGS}
      onSave={onSave}
      onClose={onClose}
    >
      {isFlatRateMethod(calculationMethod, inputs) ? (
        <>
          <ListRow>
            <Col>
              <Text strong>Berechnung</Text>
            </Col>
          </ListRow>

          <ListRow>
            <Col flex="1">An wie vielen Tagen genutzt</Col>
            <Col>{inputs.daysUsed}</Col>
          </ListRow>
        </>
      ) : (
        <>
          <ListRow>
            <Col flex="1">Art der Wohnimmobilie</Col>
            <Col>{HOME_OWNERSHIP_TYPE_LABELS[inputs.homeOwnershipType]}</Col>
          </ListRow>

          <Divider className="m-0" />

          <ListRow>
            <Col>
              <Text strong>Angaben Wohnung/Haus</Text>
            </Col>
          </ListRow>

          <ListRow>
            <Col flex="1">Gesamtfläche</Col>
            <Col>{inputs.totalHomeArea}</Col>
          </ListRow>
          <ListRow>
            <Col flex="1">Arbeitszimmer Fläche</Col>
            <Col>{inputs.officeArea}</Col>
          </ListRow>
          {officeAreaShare !== undefined && (
            <ListRow>
              <Col flex="1">Anteil Arbeitszimmer Gesamtfläche</Col>
              <Col>{Math.round(officeAreaShare * 100)} %</Col>
            </ListRow>
          )}

          <Divider className="m-0" />

          <ListRow>
            <Col>
              <Text strong>Nutzungszeitraum</Text>
            </Col>
          </ListRow>

          <ListRow>
            <Col flex="1">Nutzungsbeginn</Col>
            <Col>
              {inputs.usageStartedAt
                ? moment(inputs.usageStartedAt).format(DISPLAY_DATE_FORMAT)
                : ""}
            </Col>
          </ListRow>
          <ListRow>
            <Col flex="1">Nutzungsmonate</Col>
            <Col>{inputs.monthsUsed}</Col>
          </ListRow>

          <Divider className="m-0" />

          <ListRow>
            <Col>
              <Text strong>Ausgaben</Text>
            </Col>
          </ListRow>

          <ListRow>
            <Col flex="1">Summe Kosten Arbeitszimmer</Col>
            <Col>{formatAmountInCents(totalNetAmount, true)}</Col>
          </ListRow>

          <ListRow>
            <Col flex="1">Summe gezahlte Vorsteuer Arbeitszimmer</Col>
            <Col>{formatAmountInCents(totalVatAmount, true)}</Col>
          </ListRow>
        </>
      )}
      <Divider className="m-0" />

      <ListRow>
        <Col>
          <Text strong>Ergebnisse für EÜR</Text>
        </Col>
      </ListRow>

      <ListRow>
        <Col flex="1">
          [172] Abziehbare Aufwendung für häusliches Arbeitszimmer (inkl. AfA
          und Schuldzinsen)
        </Col>
        <Col>
          {calculationMethod ===
          EuerDeclarationSubformCalculationMethod.OFFICE_USAGE_FLAT_RATE_5_EUR
            ? formatAmountInCents(calculationResults.homeOfficeExpenses, true)
            : formatAmountInCents(totalNetAmount, true)}
        </Col>
      </ListRow>

      {!isFlatRateMethod(calculationMethod, inputs) && !isSmallBusiness && (
        <ListRow>
          <Col flex="1">[185] Gezahlte Vorsteuerbeträge</Col>
          <Col>{formatAmountInCents(totalVatAmount, true)}</Col>
        </ListRow>
      )}

      <Divider className="m-0" />

      <ListRow>
        <Col flex="1">
          <Total>Gewinnminderung</Total>
        </Col>
        <Col>
          <Text
            strong
            style={{
              color:
                calculationResults.profitReduction > 0
                  ? colors.darkRed
                  : colors.darkGrey,
            }}
          >
            {calculationMethod ===
            EuerDeclarationSubformCalculationMethod.OFFICE_USAGE_FLAT_RATE_5_EUR
              ? formatAmountInCents(calculationResults.profitReduction, true)
              : formatAmountInCents(totalNetAmount + totalVatAmount, true)}
          </Text>
        </Col>
      </ListRow>
    </DeclarationStatusChangeModal>
  );
};

export default OfficeUsageStatusChangeModal;
